<template>
  <span
    v-if="span"
    v-bind="$attrs"
    :class="$style.container"
    :style="style.container"
  >
    <template v-if="truncate">
      <div :style="truncateStyle">
        <slot />
      </div>
    </template>
    <template v-else>
      <slot />
    </template>
  </span>
  <div
    v-else
    v-bind="$attrs"
    :class="$style.container"
    :style="style.container"
  >
    <template v-if="truncate">
      <div :style="truncateStyle">
        <slot />
      </div>
    </template>
    <template v-else>
      <slot />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

import { createStyle } from '@v1/composables/create-style';
import { StyleExtender } from '@v1/composables/style-extender';
import { useTruncateStyle } from '@v1/composables/use-styles';

const styleExtender = new StyleExtender({ variablePrefix: '--v1-base-text' });

const props = defineProps({
  disableTruncate: {
    type: Boolean,
    required: false,
    default: true,
  },
  inline: {
    type: Boolean,
    required: false,
    default: false,
  },
  inlineBlock: {
    type: Boolean,
    required: false,
    default: false,
  },
  nowrap: {
    type: Boolean,
    required: false,
    default: false,
  },
  span: {
    type: Boolean,
    required: false,
    default: false,
  },
  usePreline: {
    type: Boolean,
    required: false,
    default: false,
  },
  styleExtension: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  asReactive: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const truncateStyle = ref(useTruncateStyle());

const truncate = computed(() => (
  !props.disableTruncate
  && !props.inline
  && !props.inlineBlock));

const style = createStyle({
  container: {
    ...(props.usePreline ? {
      whiteSpace: 'pre-line',
    } : {}),
    ...(props.nowrap ? {
      textWrap: 'nowrap',
    } : {}),
  },
}, {
  asReactive: props.asReactive,
});

styleExtender.extend({
  target: style.container,
  extension: props.styleExtension.container,
  selector: 'container',
});

if (props.inline || props.inlineBlock) {
  style.container.display = props.inline ? 'inline' : 'inline-block';
  style.container.verticalAlign = 'top';
}
</script>

<style module lang="scss">
.container {
  background-color: var(--v1-base-text-container-background-color);
  border: var(--v1-base-text-container-border);
  border-radius: var(--v1-base-text-container-border-radius);
  border-width: var(--v1-base-text-container-border-width);
  box-shadow: var(--v1-base-text-container-box-shadow);
  color: var(--v1-base-text-container-color);
  cursor: var(--v1-base-text-container-cursor);
  font-family: var(--v1-base-text-container-font-family);
  font-size: var(--v1-base-text-container-font-size);
  font-weight: var(--v1-base-text-container-font-weight);
  text-decoration: var(--v1-base-text-container-text-decoration);

  &:hover {
    background-color: var(--v1-base-text-container-background-color-hover);
    border: var(--v1-base-text-container-border-hover);
    border-radius: var(--v1-base-text-container-border-radius-hover);
    border-width: var(--v1-base-text-container-border-width-hover);
    box-shadow: var(--v1-base-text-container-box-shadow-hover);
    color: var(--v1-base-text-container-color-hover);
    cursor: var(--v1-base-text-container-cursor-hover);
    font-family: var(--v1-base-text-container-font-family-hover);
    font-size: var(--v1-base-text-container-font-size-hover);
    font-weight: var(--v1-base-text-container-font-weight-hover);
    text-decoration: var(--v1-base-text-container-text-decoration-hover);
  }

  &:active {
    background-color: var(--v1-base-text-container-background-color-active);
    border: var(--v1-base-text-container-border-active);
    border-radius: var(--v1-base-text-container-border-radius-active);
    border-width: var(--v1-base-text-container-border-width-active);
    box-shadow: var(--v1-base-text-container-box-shadow-active);
    color: var(--v1-base-text-container-color-active);
    cursor: var(--v1-base-text-container-cursor-active);
    font-family: var(--v1-base-text-container-font-family-active);
    font-size: var(--v1-base-text-container-font-size-active);
    font-weight: var(--v1-base-text-container-font-weight-active);
    text-decoration: var(--v1-base-text-container-text-decoration-active);
  }
}
</style>
